"use client";

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

interface DeleteDialogProps {
  blockId: Id<"blocks">;
  blockDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteDialog({
  blockId,
  blockDate,
  open,
  onOpenChange,
}: DeleteDialogProps) {
  const block = useQuery(api.blocks.getBlock, open ? { id: blockId } : "skip");
  const deleteBlock = useMutation(api.blocks.deleteBlock);
  const createBlock = useMutation(api.blocks.createBlock);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!block) return;

    // Store block data before deletion
    const blockData = {
      date: block.date,
      shiftStart: block.shiftStart,
      shiftEnd: block.shiftEnd,
      basePay: block.basePay,
      tip: block.tip,
      noRoutePayout: block.noRoutePayout,
      manualMiles: block.manualMiles,
      notes: block.notes,
    };

    setIsDeleting(true);
    try {
      await deleteBlock({ id: blockId });
      onOpenChange(false);

      toast({
        title: "Block deleted",
        description: `${blockDate} block removed`,
        duration: 5000,
        action: (
          <ToastAction
            altText="Undo delete"
            onClick={async () => {
              try {
                await createBlock(blockData);
                toast({
                  title: "Block restored",
                  description: `${blockDate} block has been restored`,
                });
              } catch {
                toast({
                  title: "Failed to restore",
                  description: "Could not restore the block",
                  variant: "destructive",
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Block</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the block from {blockDate}?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || !block}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
